/**
 * The HRT / TRT sections more than one tab shows: the pending decisions banner, Safety and Readiness (design step
 * 2), the controlled-medicine notice, the Clinical Twin panel (step 3) and the monitoring schedule (step 4).
 *
 * All words and values are the API's. The controlled-medicine notice is the design's sentence, with the schedule
 * taken from the formulary row and the telemedicine dates taken from the engine's REG-006 text, so a change to
 * either reaches the screen without a code change.
 */

import type { ReactNode } from 'react';
import { IconBars, IconShield } from './icons';
import { InfoHint } from './ui';
import { SourceChip, StatusMark, StepCard } from './HrtUi';
import {
  MONITORING_STATE,
  dueWindow,
  findingFor,
  onFileLine,
  pathwaySchedule,
  ruleSource,
  shortDay,
  type HrtPatientView,
  type MonitoringRow,
} from '../lib/hrt';

/* ------------------------------------------------------------------ *
 * Pending decisions
 * ------------------------------------------------------------------ */

/** Developer spec tab "Output": pendingDecisions "Show as a banner while not empty". */
export function PendingDecisionsBanner({ decisions }: { decisions: HrtPatientView['pendingDecisions'] }) {
  if (decisions.length === 0) return null;
  const rows = decisions.map((d) => d.row).join(', ');
  return (
    <div
      className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[10px] border border-plum-line bg-plum-soft px-3.5 py-2 text-[13px] text-plum-ink"
      role="status"
      data-testid="pending-decisions"
    >
      <span className="font-semibold">
        {decisions.length === 1 ? '1 rule is' : `${decisions.length} rules are`} waiting on Dr. Joe (decisions {rows}).
      </span>
      <span className="text-plum">MCOS keeps them exactly as written in v1.2 until he decides.</span>
      <InfoHint label="The decisions waiting on Dr. Joe">
        {decisions.map((d) => (
          <span key={d.row} className="mb-1 block last:mb-0">
            <b>
              {d.row}. {d.topic}
            </b>
            : {d.kept}
          </span>
        ))}
      </InfoHint>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step 2: Safety and Readiness
 * ------------------------------------------------------------------ */

export function ReadinessStep({ view, pathwayId, step = 2 }: { view: HrtPatientView; pathwayId: string | null; step?: number }) {
  const rows = view.readiness.rows;
  return (
    <StepCard step={step} title="Safety and Readiness" subtitle="Answered by MCOS from the intake and labs. Each line shows where it came from." testId="hrt-readiness">
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-hairline px-4 py-5 text-[13.5px] text-ink-muted">
          Nothing to check yet. The lines appear once the intake has the hormone questions.
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {rows.map((row) => {
            const notes = row.status === 'ok' ? [] : row.fired.map((id) => findingFor(view, id)).filter((f): f is NonNullable<typeof f> => Boolean(f));
            return (
              <li key={row.id} className="flex items-start gap-2.5 rounded-lg bg-paper-deep px-2.5 py-2" data-testid={`readiness-${row.id}`}>
                <span className="mt-[1px]">
                  <StatusMark status={row.display === null && row.status === 'ok' ? 'missing' : row.status} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-2">
                    <span className="text-[12.5px] leading-snug text-ink-muted">{row.label}</span>
                    <SourceChip source={row.source} />
                  </span>
                  <span className={`mt-0.5 block text-[13.5px] leading-snug ${row.display ? 'font-semibold text-navy' : 'text-ink-muted'}`}>
                    {row.display ?? (row.required || row.status === 'locked' ? 'Not on file' : 'No answer')}
                  </span>
                  {notes.map((note) => (
                    <span key={note.id} className="mt-1 flex items-start gap-1 text-[12px] leading-snug text-risk-yellow">
                      <span>{note.text}</span>
                      <InfoHint label={`Source of ${note.name}`}>{ruleSource(note)}</InfoHint>
                    </span>
                  ))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <ControlledNotice view={view} pathwayId={pathwayId} />
    </StepCard>
  );
}

/**
 * The lavender line under Safety and Readiness. REG-006 for a controlled pathway (the one picked, else the top one)
 * and REG-001 when the patient's state is missing. Regulatory flags never change relevance; they gate prescribing.
 */
export function ControlledNotice({ view, pathwayId }: { view: HrtPatientView; pathwayId: string | null }) {
  const lines: Array<{ id: string; text: ReactNode }> = [];
  for (const reg of view.readiness.regulatory) {
    if (reg.id === 'REG-006') {
      if (!pathwayId || !reg.pathwayIds.includes(pathwayId)) continue;
      const schedule = pathwaySchedule(view.products[pathwayId]);
      const through = /through ([A-Z][a-z]{2} \d{1,2}, \d{4})/.exec(reg.text)?.[1] ?? null;
      const before = /before ([A-Z][a-z]{2} \d{1,2}, \d{4})/.exec(reg.text)?.[1] ?? null;
      const allowed = !/PHARMACY REVIEW REQUIRED/.test(reg.text) && through && before;
      lines.push({
        id: reg.id,
        text: (
          <>
            Testosterone is a {schedule ? `${schedule} ` : ''}controlled medicine: it is prescribed through DoseSpot with EPCS.{' '}
            {allowed ? `Telehealth-only prescribing is allowed through ${through} (DEA extension); MCOS re-checks this before ${before}.` : reg.text}
          </>
        ),
      });
    } else {
      lines.push({ id: reg.id, text: reg.text });
    }
  }
  if (lines.length === 0) return null;
  return (
    <div className="mt-2.5 space-y-1.5 rounded-lg bg-plum-soft px-3 py-2.5" data-testid="controlled-notice">
      {lines.map((line) => (
        <p key={line.id} className="flex gap-2.5 text-[12.5px] leading-snug text-plum-ink">
          <IconShield size={17} className="mt-[1px] shrink-0 text-plum" />
          <span>{line.text}</span>
        </p>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step 3: Clinical Twin
 * ------------------------------------------------------------------ */

/** The design's grey ring, empty: below the minimum cohort MCOS shows no figure (CLAUDE.md, Clinical Twin). */
export function TwinPanel({ view }: { view: HrtPatientView }) {
  const twin = view.twin;
  if (!twin) return null;
  const tiles = [{ id: 'matched', value: String(twin.matchedPatients), label: 'Matched patients' }, ...twin.outcomes.map((o) => ({ id: o.id, value: o.value, label: o.label }))];
  return (
    <div data-testid="hrt-twin">
      <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[170px_minmax(0,1fr)]">
        <div className="grid place-items-center py-1">
          <div className="grid h-[150px] w-[150px] place-items-center rounded-full border-[13px] border-mint text-center">
            <p className="px-2 text-[13px] leading-snug text-ink-muted">{twin.label}</p>
          </div>
        </div>
        <ul className="space-y-1.5">
          {tiles.map((tile) => (
            <li key={tile.id} className="flex items-center gap-3 rounded-md bg-paper-deep px-2 py-1.5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-mint text-forest">
                <IconBars size={18} />
              </span>
              <span className="min-w-0">
                <span className="block font-display text-[17px] leading-tight text-navy" style={{ fontWeight: 600 }}>{tile.value}</span>
                <span className="block text-[12px] leading-tight text-ink-muted">{tile.label}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-[13px] leading-snug text-ink-muted">
        <b className="font-semibold text-navy">Matched on:</b> {twin.matchedOn}
      </p>
    </div>
  );
}

export function TwinStep({ view, step = 3 }: { view: HrtPatientView; step?: number }) {
  if (!view.twin) return null;
  return (
    <StepCard step={step} title="Clinical Twin Outcome Prediction" subtitle={view.twin.summary} testId="hrt-twin-step">
      <TwinPanel view={view} />
    </StepCard>
  );
}

/* ------------------------------------------------------------------ *
 * Step 4: Monitoring after start
 * ------------------------------------------------------------------ */

function monitoringSubtitle(view: HrtPatientView): string {
  const plan = view.monitoring;
  if (!plan) return 'The follow-up visits and labs appear once a pathway is ranked.';
  const name = plan.pathwayName;
  if (plan.basis === 'top') return `For the top pathway, ${name}. The dates are set from the day therapy starts.`;
  if (plan.startDate) return `For ${name}, counted from the start on ${shortDay(plan.startDate)}.`;
  return `For ${name}, the pathway chosen. The dates are set from the day therapy starts.`;
}

export function MonitoringList({ rows }: { rows: MonitoringRow[] }) {
  return (
    <ol className="space-y-2" data-testid="hrt-monitoring">
      {rows.map((row) => {
        const state = MONITORING_STATE[row.state];
        const window = dueWindow(row);
        return (
          <li key={row.id} className="grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-lg border border-hairline px-3 py-2.5 sm:grid-cols-[180px_minmax(0,1fr)]">
            <div>
              <p className="font-display text-[15.5px] leading-tight text-navy" style={{ fontWeight: 560 }}>
                {row.when.charAt(0).toUpperCase() + row.when.slice(1)}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className={`rounded-[5px] px-1.5 py-[3px] text-[11px] font-semibold leading-none ${state.tone}`} data-testid={`monitoring-state-${row.id}`}>
                  {state.word}
                </span>
                {window && <span className="text-[12px] text-ink-muted">{row.repeats === 'every' && row.onFile.length > 0 ? `Next ${window}` : window}</span>}
              </div>
              {row.anchor === 'dose_change' && <p className="mt-1 text-[11.5px] text-ink-muted">From the last dose change</p>}
            </div>
            <div className="min-w-0 space-y-1 text-[12.5px] leading-snug text-ink-muted">
              {row.assess.length > 0 && (
                <p>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">Assess </span>
                  {row.assess.join('; ')}
                </p>
              )}
              {row.labs.length > 0 && (
                <p>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">Labs </span>
                  {row.labs.join('; ')}
                </p>
              )}
              {onFileLine(row.onFile) && (
                <p className="text-forest">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.05em]">On file </span>
                  {onFileLine(row.onFile)}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function MonitoringStep({ view, step = 4 }: { view: HrtPatientView; step?: number }) {
  const plan = view.monitoring;
  return (
    <StepCard step={step} title="Monitoring After Start" subtitle={monitoringSubtitle(view)} testId="hrt-monitoring-step">
      {plan && plan.rows.length > 0 ? (
        <MonitoringList rows={plan.rows} />
      ) : (
        <p className="rounded-lg border border-dashed border-hairline px-4 py-5 text-[13.5px] text-ink-muted">No monitoring rows for this pathway.</p>
      )}
    </StepCard>
  );
}

/* ------------------------------------------------------------------ *
 * The Clinical Twin Insights tab
 * ------------------------------------------------------------------ */

export function HrtTwinTab({ view }: { view: HrtPatientView }) {
  if (!view.twin) return null;
  return (
    <StepCard title="Clinical Twin insights" subtitle={view.twin.summary} testId="hrt-twin-tab">
      <div className="max-w-[640px]">
        <TwinPanel view={view} />
      </div>
      <p className="mt-3 text-[12.5px] text-ink-muted">
        Observed outcomes in similar patients, never a prediction for this patient. Below {view.twin.minCohort} matched patients MCOS shows no
        figure.
      </p>
    </StepCard>
  );
}
